import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'src/providers/Theme';
import { runAISearch } from './aiSearchState';
import './ai-integrated.css';

const TEXT = {
  az: { title: 'AI ilə 3D modelləri tap', prompt: 'Obyekti, rəngi, materialı və ya formanı təsvir et', placeholder: 'Məsələn: açıq rəngli yumşaq kreslo', choose: 'Şəkil seç', drop: 'Şəkli buraya gətir', paste: 'Fayl seç və ya şəkli yapışdır', image: 'Şəkillə axtarış', text: 'AI axtarış', search: 'Axtar', invalid: '15 MB-dan kiçik JPG, PNG və ya WEBP seç.', example: 'Nümunə: wooden armchair with beige upholstery' },
  ru: { title: 'Найти 3D-модели с помощью ИИ', prompt: 'Опишите объект, цвет, материал или форму', placeholder: 'Например: бежевое мягкое кресло', choose: 'Выбрать файл', drop: 'Перетащите изображение сюда', paste: 'Выберите или вставьте изображение', image: 'Поиск по картинке', text: 'ИИ поиск', search: 'Найти', invalid: 'Выберите JPG, PNG или WEBP до 15 МБ.', example: 'Пример: wooden armchair with beige upholstery' },
  en: { title: 'Find 3D models with AI', prompt: 'Describe an object, color, material or shape', placeholder: 'For example: beige upholstered armchair', choose: 'Choose image', drop: 'Drop an image here', paste: 'Choose or paste an image', image: 'Search by image', text: 'AI search', search: 'Search', invalid: 'Choose JPG, PNG or WEBP under 15 MB.', example: 'Example: wooden armchair with beige upholstery' },
};

export default function AISearchControls({ mode = 'image' }) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const language = (i18n.resolvedLanguage || 'en').split('-')[0];
  const c = TEXT[language] || TEXT.en;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const fileInput = useRef(null);
  const dialog = useRef(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector('button')?.focus();
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  function select(candidate) {
    if (!candidate) return;
    setError('');
    if (!candidate || !['image/jpeg','image/png','image/webp'].includes(candidate.type) || candidate.size > 15 * 1024 * 1024) {
      setError(c.invalid); return;
    }
    runAISearch('image', candidate, language);
    setOpen(false);
    navigate('/models');
  }
  function submit(e) {
    e?.preventDefault();
    if (mode === 'text' && query.trim().length < 2) { setError(c.prompt); return; }
    runAISearch('text', query.trim(), language);
    setOpen(false);
    navigate('/models');
  }
  return <>
    <button type='button' className={mode === 'image' ? 'oc-search-camera' : 'oc-search-ai'}
      onClick={() => { setError(''); setDrag(false); setOpen(true); }}
      aria-label={mode === 'image' ? c.image : c.text} title={mode === 'image' ? c.image : c.text}>
      {mode === 'image' ? <svg width='23' height='23' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' aria-hidden='true'><path d='M3 8h4l2-3h6l2 3h4v12H3z'/><circle cx='12' cy='13' r='4'/></svg> : <>✦ <span>{c.text}</span></>}
    </button>
    {open && createPortal(<div className={'oc-ai-backdrop ' + (theme === 'theme-dark' ? 'oc-ai-dark' : '')}
      onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
      <section className='oc-ai-modal' ref={dialog} role='dialog' aria-modal='true' aria-label={mode === 'image' ? c.image : c.title}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        onPaste={e => { if (mode === 'image') { const item = [...e.clipboardData.items].find(i => i.type.startsWith('image/')); if (item) { e.preventDefault(); select(item.getAsFile()); } } }}>
        <button type='button' className='oc-ai-close' aria-label='Close' onClick={() => setOpen(false)}>×</button>
        {mode === 'text' ? <>
          <h2>{c.title}</h2><p>{c.prompt}</p>
          <form onSubmit={submit} className='oc-ai-textform'>
            <input autoFocus maxLength={500} value={query} onChange={e => setQuery(e.target.value)} placeholder={c.placeholder} />
            <button type='submit' aria-label={c.search}>⌕</button>
          </form>
          <p className='oc-ai-example'>{c.example}</p>
        </> : <>
          <h2>{c.image}</h2>
          <div className={'oc-ai-drop ' + (drag ? 'oc-ai-drag' : '')} onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); select(e.dataTransfer.files?.[0]); }}>
            <><div className='oc-ai-picture' aria-hidden='true'>▧</div><h3>{c.drop}</h3><p>{c.paste}</p></>
            <input ref={fileInput} type='file' hidden accept='image/jpeg,image/png,image/webp' onChange={e => { select(e.target.files?.[0]); e.target.value=''; }} />
            <button type='button' className='oc-ai-choose' onClick={() => fileInput.current?.click()}>{c.choose}</button>
          </div>
        </>}
        {error && <p role='alert' className='oc-ai-error'>{error}</p>}
      </section>
    </div>, document.body)}
  </>;
}
